import { collection, deleteDoc, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';

import { ICreateDiplomaCodeData, IDiplomaCode } from '@customTypes/diploma';
import { IGroup } from '@customTypes/group';
import { IUser } from '@customTypes/user';
import { db } from 'external/firebase/index';

export const generateSimpleDiplomaCode = (): string => {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 8);
    return `CAKD-${timestamp}${randomPart}`.toUpperCase();
};

export const createDiplomaCode = async (data: ICreateDiplomaCodeData): Promise<string> => {
    const docId = `${data.studentId}_${data.groupId}`;

    const diplomaCodeData = {
        studentId: data.studentId,
        groupId: data.groupId,
        code: data.code,
    };

    await setDoc(doc(db, 'diplomaCodes', docId), diplomaCodeData);
    return docId;
};

export const getDiplomaCode = async (studentId: string, groupId: string): Promise<IDiplomaCode | null> => {
    const docId = `${studentId}_${groupId}`;
    const docRef = doc(db, 'diplomaCodes', docId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
        return null;
    }

    return { id: docSnap.id, ...docSnap.data() } as IDiplomaCode;
};

export const getStudentDiplomaCodes = async (studentId: string): Promise<IDiplomaCode[]> => {
    const q = query(collection(db, 'diplomaCodes'), where('studentId', '==', studentId));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() } as IDiplomaCode));
};

export const getGroupDiplomaCodes = async (groupId: string): Promise<IDiplomaCode[]> => {
    const q = query(collection(db, 'diplomaCodes'), where('groupId', '==', groupId));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() } as IDiplomaCode));
};

export const generateDiplomaCodesForGroup = async (group: IGroup, students: IUser[]): Promise<void> => {
    const promises = students.map(async student => {
        const existingCode = await getDiplomaCode(student.id, group.id);
        if (existingCode) {
            return; // Skip if already exists
        }

        const code = generateSimpleDiplomaCode();

        await createDiplomaCode({
            studentId: student.id,
            groupId: group.id,
            code,
        });
    });

    await Promise.all(promises);
};

export const generateDiplomaCodeForStudentGroup = async (studentId: string, groupId: string): Promise<string> => {
    const existingCode = await getDiplomaCode(studentId, groupId);
    if (existingCode) {
        return existingCode.code;
    }

    const code = generateSimpleDiplomaCode();

    await createDiplomaCode({
        studentId,
        groupId,
        code,
    });

    return code;
};

export const deleteDiplomaCode = async (studentId: string, groupId: string): Promise<void> => {
    const docId = `${studentId}_${groupId}`;
    const docRef = doc(db, 'diplomaCodes', docId);
    await deleteDoc(docRef);
};

export const deleteDiplomaCodesForStudents = async (studentIds: string[], groupId: string): Promise<void> => {
    const deletePromises = studentIds.map(studentId => deleteDiplomaCode(studentId, groupId));
    await Promise.all(deletePromises);
};
